// src/App.jsx
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { LogProvider } from "./context/LogContext";
import { UserProvider } from "./context/UserContext";

import Feedback from "./screens/Feedback";
import Dashboard from "./screens/Dashboard";
import Drivers from "./screens/Drivers";
import FoodOrders from "./screens/FoodOrders";
import FoodRestaurants from "./screens/FoodRestaurants";
import FoodTypes from "./screens/FoodTypes";
import Login from "./screens/Login";
import Logs from "./screens/Logs";
import MartProducts from "./screens/MartProducts";
import MasterManagement from "./screens/MasterManagement";
import Notifications from "./screens/Notifications";
import Passengers from "./screens/Passengers";
import ProductOrder from "./screens/ProductOrder";
import ProductTypes from "./screens/ProductTypes";
import Profile from "./screens/Profile";
import ResetPassword from "./screens/ResetPassword";
import Revenue from "./screens/Revenue";
import Rides from "./screens/Rides";
import RideTypes from "./screens/RideTypes";
import RoleManagement from "./screens/RoleManagement";
import ServiceCategories from "./screens/ServiceCategories";
import Settings from "./screens/Settings";

import MainLayout from "./components/MainLayout";

// NEW: Users page
import Users from "./screens/Users";

// Driver Approval
import DriverApproval from "./screens/DriverApproval";

// NEW: Wallet screen (under screens, not pages)
import Wallet from "./screens/Wallet";

// ✅ NEW: Withdrawals (Admin)
import Withdrawals from "./screens/Withdrawals";

// NEW: PG Log screen (same folder as Wallet)
import PGLog from "./screens/PGLog";

// ✅ NEW: Finance Reports
import FinanceReports from "./screens/FinanceReports";

// NEW: Master Management sub-pages
import PointsManagement from "./screens/PointsManagement";
import TaxManagement from "./screens/TaxManagement";

// ✅ NEW: Platform Fee Management screen
import PlatformFeeManagement from "./screens/PlatformFeeManagement";

// ✅ NEW: Offers and Rewards screen
import OffersAndRewards from "./screens/OffersAndRewards";

// NEW: Logo / Image Management
import LogoImageManagement from "./screens/LogoImageManagement";

// NEW: Services – Events, Dining Out, Hotel
import EventsManagement from "./screens/EventsManagement";
import EventsBookingsList from "./screens/EventsBookingsList";
import EventsRevenue from "./screens/EventsRevenue";
import EventsBanners from "./screens/EventsBanners";
import EventsOrganizers from "./screens/EventsOrganizers";
import OrganizerRevenueDetail from "./screens/OrganizerRevenueDetail";
import DiningReservations from "./screens/DiningReservations";
import DiningRestaurantTypes from "./screens/DiningRestaurantTypes";
import HotelBookings from "./screens/HotelBookings";
import HotelRoomTypes from "./screens/HotelRoomTypes";

function isSuperAdmin() {
  try {
    const v = localStorage.getItem("is_super");
    if (v === "true" || v === "1") return true;
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
    const roleNorm = String(raw).toLowerCase().replace(/\s+/g, " ").trim();
    const roleCompact = roleNorm.replace(/\s+/g, "");
    return roleNorm === "super admin" || roleCompact === "superadmin";
  } catch {
    return false;
  }
}

function isFinance() {
  try {
    if (localStorage.getItem("is_finance") === "true") return true;
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const raw = user?.role ?? user?.user_role ?? user?.role_name ?? user?.type ?? "";
    return String(raw).toLowerCase().replace(/\s+/g, " ").trim() === "finance";
  } catch {
    return false;
  }
}


// Super Admin only
function RequireSuper({ children }) {
  return isSuperAdmin() ? children : <Navigate to="/dashboard" replace />;
}

// Block finance users from this route
function RequireNotFinance({ children }) {
  return isFinance() ? <Navigate to="/dashboard" replace /> : children;
}

function App() {
  return (
    <UserProvider>
      <BrowserRouter>
        <LogProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected routes inside main layout */}
            <Route element={<MainLayout />}>
              {/* Core */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/users" element={<RequireNotFinance><Users /></RequireNotFinance>} />
              <Route path="/revenue" element={<Revenue />} />
              <Route path="/feedback" element={<RequireNotFinance><Feedback /></RequireNotFinance>} />
              <Route path="/logs" element={<Logs />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<RequireNotFinance><Settings /></RequireNotFinance>} />

              {/* Finance */}
              <Route
                path="/finance/wallet"
                element={
                  <RequireSuper>
                    <Wallet />
                  </RequireSuper>
                }
              />
              <Route path="/finance/withdrawals" element={<Withdrawals />} />
              <Route path="/finance/pg-log" element={<PGLog />} />
              <Route path="/finance/reports" element={<FinanceReports />} />

              {/* Management — accessible to all roles including Finance */}
              <Route path="/master-management" element={<MasterManagement />} />
              <Route path="/master-management/role-management" element={<RoleManagement />} />
              <Route path="/master-management/service-categories" element={<ServiceCategories />} />
              <Route path="/master-management/notifications" element={<Notifications />} />
              <Route path="/master-management/points-management" element={<PointsManagement />} />
              <Route path="/master-management/tax-management" element={<TaxManagement />} />
              <Route path="/master-management/platform-fee-management" element={<PlatformFeeManagement />} />
              <Route path="/master-management/offers-and-rewards" element={<OffersAndRewards />} />
              <Route path="/master-management/logo-management" element={<LogoImageManagement />} />

              {/* SERVICES: TRANSPORT */}
              <Route path="/services/transport/driver-approvals" element={<RequireNotFinance><DriverApproval /></RequireNotFinance>} />
              <Route path="/services/transport/drivers" element={<RequireNotFinance><Drivers /></RequireNotFinance>} />
              <Route
                path="/services/transport/passengers"
                element={<RequireNotFinance><Passengers /></RequireNotFinance>}
              />
              <Route path="/services/transport/rides" element={<Rides />} />
              <Route
                path="/services/transport/ride-types"
                element={<RequireNotFinance><RideTypes /></RequireNotFinance>}
              />

              {/* SERVICES: FOOD */}
              <Route
                path="/services/food/restaurants"
                element={<RequireNotFinance><FoodRestaurants /></RequireNotFinance>}
              />
              <Route path="/services/food/orders" element={<FoodOrders />} />
              <Route path="/services/food/food-types" element={<RequireNotFinance><FoodTypes /></RequireNotFinance>} />

              {/* SERVICES: MART */}
              <Route path="/services/mart/products" element={<RequireNotFinance><MartProducts /></RequireNotFinance>} />
              <Route path="/services/mart/orders" element={<ProductOrder />} />
              <Route
                path="/services/mart/product-types"
                element={<RequireNotFinance><ProductTypes /></RequireNotFinance>}
              />

              {/* SERVICES: EVENTS & ENTERTAINMENT */}
              {/* Events: Finance can view Bookings + Revenue only */}
              <Route path="/services/events"            element={<RequireNotFinance><EventsManagement /></RequireNotFinance>} />
              <Route path="/services/events/bookings"   element={<EventsBookingsList />} />
              <Route path="/services/events/revenue"    element={<RequireNotFinance><EventsRevenue /></RequireNotFinance>} />
              <Route path="/services/events/revenue/organizer/:id" element={<RequireNotFinance><OrganizerRevenueDetail /></RequireNotFinance>} />
              <Route path="/services/events/banners"    element={<RequireNotFinance><EventsBanners /></RequireNotFinance>} />
              <Route path="/services/events/organizers" element={<RequireNotFinance><EventsOrganizers /></RequireNotFinance>} />

              {/* SERVICES: DINING OUT */}
              <Route
                path="/services/dining/reservations"
                element={<RequireNotFinance><DiningReservations /></RequireNotFinance>}
              />
              <Route
                path="/services/dining/restaurant-types"
                element={<RequireNotFinance><DiningRestaurantTypes /></RequireNotFinance>}
              />

              {/* SERVICES: HOTEL */}
              <Route
                path="/services/hotel/bookings"
                element={<RequireNotFinance><HotelBookings /></RequireNotFinance>}
              />
              <Route
                path="/services/hotel/room-types"
                element={<RequireNotFinance><HotelRoomTypes /></RequireNotFinance>}
              />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LogProvider>
      </BrowserRouter>
    </UserProvider>
  );
}

export default App;